import React, { useEffect, useRef, useState } from 'react';
import { forecastSection } from '../mock';

/**
 * Forecast — a before/after impact reveal.
 *  - A toggle cycles between "WITHOUT GROCERA" and "WITH GROCERA".
 *  - A chart of "planned vs actual demand" morphs between chaotic (red gaps)
 *    and aligned (blue). Impact stats animate to their target values.
 *  - Auto-cycles every 4.5s; user can also click the toggle.
 */

const HOURS = ['06', '08', '10', '12', '14', '16', '18', '20', '22', '00'];

// Two sets of bar values (planned demand baskets per 2-hour block)
const dataAfter = [30, 55, 90, 140, 180, 160, 220, 260, 190, 110];
// Without Grocera the plan is flatter/misaligned and doesn't track spikes
const dataBefore = [90, 110, 120, 130, 130, 130, 130, 130, 120, 110];
// "Actual" demand is the same as "after" (Grocera nails it)
const dataActual = dataAfter;

const beforeStats = [
  { label: 'Cases over-ordered', value: 142, prefix: '+', tone: 'bad' },
  { label: 'Staff hours over-planned', value: 58, prefix: '+', tone: 'bad' },
  { label: 'Waste this week', value: 1240, prefix: '$', tone: 'bad' },
  { label: 'Manual overrides', value: 14, prefix: '', tone: 'bad' },
  { label: 'Missed demand · lost sales', value: 3200, prefix: '$', tone: 'bad' },
];

const afterStats = [
  { label: 'Cases in · right sized', value: 73, prefix: '+', tone: 'good' },
  { label: 'Staff hours · right sized', value: 35, prefix: '+', tone: 'good' },
  { label: 'Waste this week', value: 180, prefix: '$', tone: 'good' },
  { label: 'Manual overrides', value: 0, prefix: '', tone: 'good' },
  { label: 'Earned · vs last week', value: 4180, prefix: '$', tone: 'good' },
];

const impactDeltas = [
  { label: 'Waste reduced', delta: '-$1,060', accent: true },
  { label: 'Overrides eliminated', delta: '-14', accent: true },
  { label: 'Extra earned', delta: '+$4,180', accent: true },
  { label: 'Lost sales recovered', delta: '+$3,200', accent: true },
];

const useCounter = (target, active, dur = 900) => {
  const [v, setV] = useState(target);
  const startVal = useRef(target);
  useEffect(() => {
    startVal.current = v;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(startVal.current + (target - startVal.current) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, active]);
  return v;
};

const StatCell = ({ s, active }) => {
  const v = useCounter(s.value, active);
  const display =
    s.prefix === '$'
      ? `$${Math.round(v).toLocaleString()}`
      : `${s.prefix}${Math.round(v).toLocaleString()}`;
  return (
    <div className="card-line p-5 min-h-[110px] flex flex-col justify-between">
      <div className="mono text-[10.5px] text-white/55 uppercase tracking-wider">{s.label}</div>
      <div
        className="h-display text-[36px]"
        style={{ color: s.tone === 'good' ? '#2E6BFF' : '#ff6b6b' }}
      >
        {display}
      </div>
    </div>
  );
};

const Chart = ({ mode }) => {
  // planned = mode==='after' ? dataAfter : dataBefore
  const planned = mode === 'after' ? dataAfter : dataBefore;
  const maxV = Math.max(...dataActual, ...planned) * 1.1;
  const W = 620;
  const H = 240;
  const pad = { l: 30, r: 12, t: 14, b: 24 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const bw = iw / dataActual.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      <defs>
        <linearGradient id="fActualGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7ba0ff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#2E6BFF" stopOpacity="0.5" />
        </linearGradient>
      </defs>

      {/* Y grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((g, i) => (
        <line
          key={i}
          x1={pad.l}
          x2={W - pad.r}
          y1={pad.t + ih * (1 - g)}
          y2={pad.t + ih * (1 - g)}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="1"
          strokeDasharray="2 4"
        />
      ))}

      {/* Actual demand (light grey area outline) */}
      <path
        d={dataActual
          .map((d, i) => {
            const x = pad.l + i * bw + bw / 2;
            const y = pad.t + ih * (1 - d / maxV);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          })
          .join(' ')}
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />

      {/* Planned bars */}
      {planned.map((d, i) => {
        const x = pad.l + i * bw + 4;
        const w = bw - 8;
        const targetH = ih * (d / maxV);
        const y = pad.t + ih - targetH;
        // Gap indicator vs actual
        const actual = dataActual[i];
        const gap = Math.abs(actual - d);
        const isBad = mode === 'before' && gap > 30;
        return (
          <g key={i}>
            {isBad && (
              <rect
                x={x}
                y={pad.t + ih * (1 - actual / maxV)}
                width={w}
                height={Math.abs(ih * (actual / maxV) - targetH)}
                fill="rgba(255, 107, 107, 0.18)"
                stroke="rgba(255,107,107,0.55)"
                strokeDasharray="3 3"
                strokeWidth="1"
                style={{ transition: 'all 700ms cubic-bezier(0.22,0.61,0.36,1)' }}
              />
            )}
            <rect
              x={x}
              y={y}
              width={w}
              height={targetH}
              rx="2"
              fill={mode === 'after' ? 'url(#fActualGrad)' : 'rgba(255,255,255,0.12)'}
              stroke={mode === 'after' ? 'rgba(46,107,255,0.5)' : 'rgba(255,255,255,0.25)'}
              strokeWidth="1"
              style={{ transition: 'all 700ms cubic-bezier(0.22,0.61,0.36,1)' }}
            />
          </g>
        );
      })}

      {/* X labels */}
      {HOURS.map((h, i) => (
        <text
          key={h}
          x={pad.l + i * bw + bw / 2}
          y={H - 6}
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          fill="rgba(255,255,255,0.45)"
        >
          {h}
        </text>
      ))}

      {/* Legend */}
      <g transform={`translate(${W - 210}, 8)`}>
        <rect x="0" y="-6" width="12" height="8" fill={mode === 'after' ? 'url(#fActualGrad)' : 'rgba(255,255,255,0.14)'} stroke={mode === 'after' ? 'rgba(46,107,255,0.5)' : 'rgba(255,255,255,0.25)'} />
        <text x="18" y="1" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.7)">
          PLANNED
        </text>
        <line x1="86" y1="-2" x2="106" y2="-2" stroke="rgba(255,255,255,0.55)" strokeDasharray="3 3" strokeWidth="1.5" />
        <text x="112" y="1" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.7)">
          ACTUAL DEMAND
        </text>
      </g>
    </svg>
  );
};

export default function Forecast() {
  const [mode, setMode] = useState('before'); // 'before' | 'after'
  const [flashing, setFlashing] = useState(false);
  const ref = useRef(null);

  // Auto-cycle when section is in view
  useEffect(() => {
    let inView = false;
    let id;
    const io = new IntersectionObserver(
      ([e]) => {
        inView = e.isIntersecting;
      },
      { threshold: 0.35 }
    );
    if (ref.current) io.observe(ref.current);
    id = setInterval(() => {
      if (!inView) return;
      setFlashing(true);
      setTimeout(() => setFlashing(false), 380);
      setMode((m) => (m === 'before' ? 'after' : 'before'));
    }, 4500);
    return () => {
      clearInterval(id);
      io.disconnect();
    };
  }, []);

  const stats = mode === 'after' ? afterStats : beforeStats;

  return (
    <section
      id="forecast"
      ref={ref}
      className="section relative text-white overflow-hidden border-t border-white/5"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${forecastSection.bg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(1) brightness(0.28) contrast(1.05)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050506]/85 via-[#050506]/70 to-[#050506]" />
      <div className="halftone" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">{forecastSection.eyebrow} · impact</p>
            <h2 className="h-display text-[54px] md:text-[68px] leading-[1.02] text-white max-w-[600px]">
              The day changes.<br />
              <span className="text-white/40">The plan </span>
              <span
                className="transition-colors duration-500"
                style={{ color: mode === 'after' ? '#2E6BFF' : '#ff6b6b' }}
              >
                {mode === 'after' ? 'moved first.' : 'gets stuck.'}
              </span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-10 space-y-5">
            <p className="text-white/60 text-[15px] leading-[1.7] max-w-[520px]">
              A storm midweek, a local festival, a produce price change, a limited time promo.
              Grocera recuts the orders, the labor, and the receiving windows for every store
              before any of it lands.
            </p>
            {/* Before/After Toggle */}
            <div
              className="inline-flex items-center gap-1 border border-white/10 rounded-md bg-black/40 backdrop-blur-sm p-1"
              role="tablist"
            >
              {[
                { k: 'before', label: 'Without Grocera' },
                { k: 'after', label: 'With Grocera' },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setMode(t.k)}
                  className="relative px-4 py-2 mono text-[11px] tracking-wider uppercase transition-colors"
                  style={{
                    color: mode === t.k ? '#ffffff' : 'rgba(255,255,255,0.6)',
                    background: mode === t.k ? (t.k === 'after' ? '#2E6BFF' : '#ff6b6b') : 'transparent',
                    borderRadius: 4,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div
          className="mt-14 card-line p-6 relative overflow-hidden"
          style={{
            boxShadow: flashing
              ? mode === 'after'
                ? '0 0 0 1px rgba(46,107,255,0.6), 0 0 40px rgba(46,107,255,0.35)'
                : '0 0 0 1px rgba(255,107,107,0.5), 0 0 40px rgba(255,107,107,0.25)'
              : 'none',
            transition: 'box-shadow 400ms ease',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span
                className="pill"
                style={{
                  borderColor: mode === 'after' ? 'rgba(46,107,255,0.5)' : 'rgba(255,107,107,0.5)',
                  color: mode === 'after' ? '#7ba0ff' : '#ff9a9a',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: mode === 'after' ? '#2E6BFF' : '#ff6b6b' }}
                />
                {mode === 'after' ? 'AFTER · GROCERA' : 'BEFORE · MANUAL'}
              </span>
              <span className="eyebrow">Planned vs actual demand · wed</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {forecastSection.chips.map((c) => (
                <span key={c} className="pill">{c}</span>
              ))}
            </div>
          </div>
          <Chart mode={mode} />

          {/* Overlay callout for "Grocera intervenes" moment */}
          {mode === 'after' && (
            <div
              className="absolute right-6 bottom-4 mono text-[10.5px] text-[#7ba0ff] uppercase tracking-widest"
              style={{ animation: 'blink 1.6s steps(2) 2' }}
            >
              · grocera recut the plan ·
            </div>
          )}
        </div>

        {/* Impact stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {stats.map((s, i) => (
            <StatCell key={`${mode}-${i}`} s={s} active={mode} />
          ))}
        </div>

        {/* Delta impact strip */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {impactDeltas.map((d) => (
            <div
              key={d.label}
              className="border border-[#2E6BFF]/30 rounded-md p-4 bg-[#2E6BFF]/[0.06] transition-opacity duration-500"
              style={{ opacity: mode === 'after' ? 1 : 0.15 }}
            >
              <div className="mono text-[10px] text-white/55 uppercase tracking-wider mb-1">
                {d.label}
              </div>
              <div className="h-display text-[28px] text-[#2E6BFF]">{d.delta}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
